import type { ReactNode } from "react";

interface PageHeaderProps {
  eyebrow?: string;
  title: ReactNode;
  italic?: ReactNode;
  lede?: ReactNode;
  right?: ReactNode;
  maxWidth?: number;
}

export function PageHeader({
  eyebrow,
  title,
  italic,
  lede,
  right,
  maxWidth = 720,
}: PageHeaderProps) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-10">
      <div style={{ maxWidth }}>
        {eyebrow && (
          <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-3.5">
            {eyebrow}
          </div>
        )}
        <h1 className="text-4xl md:text-[56px] leading-[1.02] tracking-[-0.035em] font-bold m-0">
          {title}
          {italic && (
            <>
              {" "}
              <span className="text-primary italic">{italic}</span>
            </>
          )}
        </h1>
        {lede && (
          <p className="text-base md:text-[17px] text-default-600 leading-normal mt-4 max-w-[560px]">
            {lede}
          </p>
        )}
      </div>
      {right}
    </header>
  );
}

interface HairlineStatsProps {
  items: {
    value: ReactNode;
    label: string;
    accent?: "primary" | "warning" | "success" | "danger";
  }[];
}

export function HairlineStats({ items }: HairlineStatsProps) {
  return (
    <div
      className="grid border-y border-divider"
      style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}
    >
      {items.map((s, i) => {
        const valueColor =
          s.accent === "primary"
            ? "text-primary"
            : s.accent === "warning"
              ? "text-warning-700"
              : s.accent === "success"
                ? "text-success-700"
                : s.accent === "danger"
                  ? "text-danger-700"
                  : "text-default-900";
        return (
          <div
            key={s.label}
            className={`px-6 py-5 ${i > 0 ? "border-l border-divider" : ""}`}
          >
            <div
              className={`text-2xl md:text-[26px] font-bold tracking-[-0.02em] ${valueColor}`}
            >
              {s.value}
            </div>
            <div className="text-tiny text-default-500 mt-1">{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

interface IndigoBlockProps {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}

export function IndigoBlock({
  children,
  className = "",
  padded = true,
}: IndigoBlockProps) {
  return (
    <div
      className={`bg-primary text-primary-foreground rounded-[20px] ${
        padded ? "p-12" : ""
      } ${className}`}
    >
      {children}
    </div>
  );
}
